import type { ComponentPropsWithoutRef, ReactNode, Ref } from 'react'
import { cn } from '#utils'

export interface IconCircleProps extends ComponentPropsWithoutRef<'div'> {
  children: ReactNode
  ref?: Ref<HTMLDivElement>
}

/**
 * Gradient circle behind a door-access status icon.
 *
 * Decorative — the icon it wraps carries whatever meaning there is, and on every access
 * surface that meaning is also in the heading beside it. No role, no label.
 *
 * ## Why this is not `primitives/data-display/GradientCircle`
 *
 * They look like the same component and are not. `GradientCircle` takes a required `variant`
 * from `gradientVariants`, a fixed table of hex pairs (beige, gold, diamond, platinum…) shared
 * with `GradientOverlay` for membership tiers. This one paints from `--theme-*`, the CLUB's
 * colour, which is a CSS custom property and cannot be expressed as a hex entry in that table.
 *
 * Folding them together would mean special-casing a `theme` variant inside a primitive whose
 * entire model is "look up two hex values", and widening the shared `GradientVariant` union
 * with a member every other consumer must then ignore. The duplication here is ~6 lines; that
 * change is not worth it for a decorative circle. Revisit if the design system grows a general
 * answer for theme-driven gradients.
 *
 * @example
 * ```tsx
 * <IconCircle className="w-24 h-24 mb-6">
 *   <DoorIcon className="w-10 h-10 text-white" />
 * </IconCircle>
 * ```
 */
export function IconCircle({ children, className, ref, ...props }: IconCircleProps) {
  return (
    <div
      aria-hidden="true"
      ref={ref}
      className={cn(
        'flex items-center justify-center rounded-full bg-gradient-to-br from-theme-200 to-theme-400',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  )
}
